import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { FileText, Upload, Download, Trash2, X, Loader2, AlertCircle } from 'lucide-react';

const MAX_BYTES = 15 * 1024 * 1024;

const fmtSize = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export const SessionResources = ({ sessionId, user, isOpen, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const fileRef = useRef(null);

  const canUpload = user?.role === 'care_provider' || user?.role === 'admin' || user?.base_role === 'admin';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/sessions/${sessionId}/resources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data || []);
    } catch (e) {
      setError('Could not load shared resources.');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen) fetchItems();
  }, [isOpen, fetchItems]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setError(null);

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setError('Only PDF files can be shared.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('That PDF is larger than the 15 MB limit.');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/sessions/${sessionId}/resources`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('PDF shared with the care seeker');
      fetchItems();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const download = async (item) => {
    setDownloadingId(item.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/resources/${item.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Could not open the file. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove "${item.original_filename}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/resources/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Resource removed');
      fetchItems();
    } catch (e) {
      toast.error('Could not remove the file.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4" data-testid="session-resources-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-[#7D202B] text-white sm:rounded-t-2xl">
          <div className="flex items-center gap-2">
            <FileText size={20} />
            <h2 className="text-base font-semibold">Shared resources</h2>
          </div>
          <button onClick={onClose} data-testid="resources-close" aria-label="Close" className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-white/10">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {canUpload && (
            <div className="mb-4">
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleFile}
                className="hidden"
                data-testid="resource-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="resource-upload-btn"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[#DCA593] text-[#7D202B] hover:bg-[#FDF2F0] transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                {uploading ? 'Uploading…' : 'Share a PDF (max 15 MB)'}
              </button>
            </div>
          )}

          {error && (
            <div className="mb-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="resource-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 size={26} className="animate-spin text-[#7D202B]" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500" data-testid="resources-empty">
              No resources shared yet.
              {canUpload ? ' Upload a PDF to share it with the care seeker.' : ' Your responder can share worksheets here.'}
            </div>
          ) : (
            <ul className="space-y-2" data-testid="resources-list">
              {items.map((it) => (
                <li key={it.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-[#FFFDFC]" data-testid={`resource-item-${it.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#FAC7C0]/40 flex items-center justify-center shrink-0">
                    <FileText size={20} className="text-[#7D202B]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#3B2923] truncate" title={it.original_filename}>{it.original_filename}</div>
                    <div className="text-xs text-gray-500">
                      {fmtSize(it.size)} · shared by {it.uploaded_by_name || 'responder'}
                    </div>
                  </div>
                  <button
                    onClick={() => download(it)}
                    disabled={downloadingId === it.id}
                    data-testid={`resource-download-${it.id}`}
                    aria-label="View / download"
                    className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full text-[#7D202B] hover:bg-[#FDF2F0]"
                  >
                    {downloadingId === it.id ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                  </button>
                  {canUpload && (
                    <button
                      onClick={() => remove(it)}
                      data-testid={`resource-delete-${it.id}`}
                      aria-label="Remove"
                      className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full text-red-500 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionResources;
